// frontend/src/components/report/VisibilityTab.js
import { useState } from "react";
import {
  Sparkles, Search, Link2, TrendingUp, ListChecks, MapPin, CheckCircle2, XCircle, Gauge, Megaphone, Lightbulb,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { GridRankMap } from "@/components/GridRankMap";
import { ReportCard } from "./primitives/ReportCard";
import KeywordTable from "./KeywordTable";
import { StatTile } from "./primitives/StatTile";
import { GroupedBarChart, youVsOthersBarItems } from "./primitives/GroupedBarChart";
import { Sparkline } from "./primitives/Sparkline";
import { PILLAR_MAX, VISIBILITY_PILLARS, cwvTone, CWV_CLS, scoreTone, highlightNames, TONE_BADGE, SEVERITY_CLS, sectionActionSummary } from "./reportUtils";

// Renders the plain-marker array highlightNames() returns (see Task 1 note)
// as real <mark> elements. highlightNames() returns the input string
// UNCHANGED (not array-wrapped) when there's nothing to mark — see its
// documented/tested contract in reportUtils.js/reportUtils.test.js — so this
// must tolerate a bare string, not just an array of parts.
function renderHighlighted(parts) {
  const arr = Array.isArray(parts) ? parts : [parts];
  return arr.map((p, i) => (typeof p === "string" ? p : <mark key={p.key ?? i} className={p.cls}>{p.text}</mark>));
}

const PROVIDER_DEFS = [
  { key: "geo_visibility", labelKey: "claudeTestTitle", consistencyKey: "claude" },
  { key: "chatgpt_visibility", labelKey: "chatgptTestTitle", consistencyKey: "chatgpt" },
  { key: "gemini_visibility", labelKey: "geminiTestTitle", consistencyKey: null },
  { key: "perplexity_visibility", labelKey: "perplexityTestTitle", consistencyKey: "perplexity" },
  { key: "google_ai_overview", labelKey: "googleAiOverviewTitle", consistencyKey: null },
  { key: "google_ai_mode", labelKey: "googleAiModeTitle", consistencyKey: null },
];

// Content-opportunity impact/effort badges — their OWN small color scheme,
// never SEVERITY_CLS: these are opportunities, not problems (see the plan's
// Global Constraints — "critical" would be meaningless here).
const IMPACT_EFFORT_CLS = {
  high: "border-indigo-200 bg-indigo-50 text-indigo-900",
  medium: "border-slate-200 bg-slate-50 text-slate-700",
  low: "border-slate-100 bg-slate-50 text-slate-500",
};

function ProviderDetail({ visibility, L, businessName, competitorNames }) {
  const [expanded, setExpanded] = useState({});
  const okQueries = (visibility.queries || []).filter((q) => !q.error);
  const citedCount = okQueries.filter((q) => q.domain_cited).length;
  return (
    <div>
      <p className="mb-3 text-base text-muted-foreground">{L.citedShareDesc(citedCount, okQueries.length)}</p>
      <ul className="space-y-2">
        {okQueries.map((q, i) => (
          <li
            key={i}
            className={`rounded-xl border p-3 text-base ${
              q.mentioned_in_answer ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            <div className="flex items-start gap-2.5">
              {q.mentioned_in_answer ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
              <span className="flex-1">
                „{q.query}"
                {q.mentioned_in_answer && q.tone && (
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${TONE_BADGE[q.tone] || TONE_BADGE.neutral}`}>
                    {L.tone[q.tone] || q.tone}
                  </span>
                )}
              </span>
            </div>
            {q.answer_excerpt && (
              <div className="mt-2 pl-6">
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
                  className="text-sm font-semibold underline underline-offset-2 opacity-80 hover:opacity-100 print:hidden"
                >
                  {expanded[i] ? L.hideAnswer : L.showAnswer}
                </button>
                {/* Always in the DOM (not a `{expanded[i] && ...}` conditional
                    render) so "Als PDF speichern" — a plain window.print(),
                    no click interaction possible on paper — can actually show
                    it: `hidden` unless toggled open on screen, `print:block`
                    unconditionally for the printed/PDF output. */}
                <p className={`mt-1.5 rounded-lg bg-white/70 p-2.5 text-sm leading-relaxed text-foreground/90 print:block ${expanded[i] ? "" : "hidden"}`}>
                  {renderHighlighted(highlightNames(q.answer_excerpt, businessName, competitorNames))}
                  {q.answer_excerpt.length >= 800 ? "…" : ""}
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// One provider's summary row (label, %, bar, "X von Y" sentence, optional
// consistency note) — used as the AccordionTrigger's content below, so the
// summary IS the collapsed row instead of a separate "Claude-
// Sichtbarkeitstest" bar up top duplicating the accordion's own
// "Claude-Sichtbarkeitstest" header further down (user feedback
// 2026-09-14: the repeated title read as duplicated data, not one summary
// leading into its own detail). GroupedBarChart's generic single `note`
// slot doesn't fit here — this needs BOTH the honest absolute-count
// sentence (aiVisibilityDesc) and, when available, a clearly-labeled
// consistency percentage right under it, two different measurements that
// must not read as one bare pair of numbers.
function ProviderBarSummary({ item, L }) {
  return (
    <div className="min-w-0 flex-1 text-left">
      <div className="mb-1 flex items-center justify-between gap-2 text-base">
        <span className="font-medium" title={item.label}>{item.label}</span>
        {!item.unavailable && (
          <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{item.value}%</span>
        )}
      </div>
      {item.unavailable ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900">
          {L.aiVisibilityUnavailable}
        </p>
      ) : (
        <>
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary" style={{ width: `${item.pct}%` }} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {L.aiVisibilityDesc(item.mentionedCount, item.totalQueries)}
            {item.note && <> · {L.consistencyLabel}: {item.note}</>}
          </p>
        </>
      )}
    </div>
  );
}

// The compact "what to do" synthesis appended at the end of one pillar's
// section — every bullet is a `.fix` string the backend already sent for a
// finding shown elsewhere on the page (see reportUtils.sectionActionSummary).
// Renders nothing when `summary` is null (no findings for that pillar, or
// none carry a fix) — no empty heading, no empty box.
function ActionSummaryBox({ summary, title }) {
  if (!summary) return null;
  return (
    <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3.5">
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-green-900/80">{title}</h3>
      <ul className="space-y-1.5">
        {summary.fixes.map((fix, i) => (
          <li key={i} className="flex items-start gap-2 text-base text-green-900">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-green-600" aria-hidden="true" />
            <span>{fix}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceAttributionSection({ visibilities, citationSites, L }) {
  const allQueries = (visibilities || []).flatMap((v) => v?.queries || []).filter((q) => !q.error);
  const freq = {};
  allQueries.forEach((q) => (q.cited_domains || []).forEach((d) => (freq[d] = (freq[d] || 0) + 1)));
  const domains = Object.keys(freq);
  if (!domains.length) return null;
  const rows = domains
    .map((d) => ({ domain: d, count: freq[d], site: (citationSites || []).find((s) => d === s.domain || d.endsWith(`.${s.domain}`)) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);
  return (
    <AccordionItem value="source-attribution" className="rounded-xl border px-3.5">
      <AccordionTrigger><span className="flex items-center gap-1.5 text-base font-semibold"><Link2 className="h-4 w-4" /> {L.sourceAttributionTitle}</span></AccordionTrigger>
      <AccordionContent>
        <p className="mb-3 text-base text-muted-foreground">{L.sourceAttributionDesc}</p>
        <ul className="space-y-2">
          {rows.map((r, i) => {
            const missing = r.site && r.site.found === false;
            return (
              <li key={i} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 text-base ${missing ? "border-amber-200 bg-amber-50" : "border-black/10 bg-secondary/30"}`}>
                <span className="font-medium">{r.domain}</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{L.sourceAttributionCount(r.count)}</span>
                  {missing && r.site.signup_url && (
                    <a href={r.site.signup_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-sm font-semibold text-white hover:bg-amber-700">
                      {L.sourceAttributionRegister}
                    </a>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function VisibilityTab({ data, lang, L, tone }) {
  const top3 = data.top3 || [];
  const competitorNames = top3.map((c) => c.label).filter(Boolean);
  const findings = data.audit_findings || [];
  const pillars = data.score_pillars || {};
  const isDetailed = !!data.is_detailed;
  const legal = data.legal;

  const providers = PROVIDER_DEFS.map((p) => ({ ...p, visibility: data[p.key] })).filter((p) => p.visibility && !p.visibility.error);
  const consistency = data.visibility_consistency || {};

  // pct here is the raw percentage itself, NOT relative to the max in this
  // set — rankBarItems() (max-normalized) would be wrong for a fixed 0-100
  // scale: if every provider sat at 10%, it would render every bar at full
  // width, visually claiming "excellent visibility" when the data says
  // "almost invisible" (regression fix, see final-review notes).
  const providerBarItems = providers
    .map((p) => {
      const v = p.visibility;
      // total_queries only counts queries that actually completed — a
      // provider whose every query errored (rate limit, timeout, ...) has
      // total_queries: 0 too, indistinguishable from "0 mentions" unless
      // checked separately. That must never render as "0%" — a failed test
      // is not evidence of poor visibility, see aiVisibilityUnavailable.
      const unavailable = !v.total_queries && (v.queries || []).some((q) => q.error);
      const pct = v.total_queries ? Math.round((v.mentioned_count / v.total_queries) * 100) : 0;
      const c = p.consistencyKey ? consistency[p.consistencyKey] : null;
      return {
        key: p.key,
        visibility: v,
        label: L[p.labelKey],
        value: pct,
        pct,
        unavailable,
        mentionedCount: v.mentioned_count,
        totalQueries: v.total_queries,
        note: c && !c.error && c.total_runs ? L.consistencyRate(c.mentioned_runs, c.total_runs, c.mentioned_rate) : null,
      };
    })
    .sort((a, b) => b.value - a.value);

  const competitorAiMentions = data.competitor_ai_mentions;
  const aiSearchVolume = Array.isArray(data.ai_search_volume) ? data.ai_search_volume : null;
  const citations = data.citations;
  const citationSites = citations && !citations.error ? citations.sites || null : null;
  const checkableCitationSites = citationSites?.filter((s) => s.checkable !== false) || null;
  const uncheckableCitationSites = citationSites?.filter((s) => s.checkable === false) || [];
  const citationsFound = checkableCitationSites ? checkableCitationSites.filter((s) => s.found).length : citations?.found_count;
  const citationsTotal = checkableCitationSites ? checkableCitationSites.length : citations?.total_count;
  const backlinks = data.backlinks;
  const pagespeed = data.pagespeed;
  const technicalSeoIssues = data.technical_seo?.issues || [];
  const adsTrackingIssues = data.ads_tracking?.issues || [];
  const mapsRank = data.maps_rank;
  const organicRank = data.organic_rank;
  const gridRank = data.grid_rank;
  const questionGaps = data.question_gaps;
  const contentRecs = isDetailed ? data.content_recs : null;
  const missingDirectories = isDetailed ? (data.content_opportunities?.missing_directories || []) : [];

  const mapsActionSummary = sectionActionSummary(findings, ["local"]);
  const aiActionSummary = sectionActionSummary(findings, ["ai"]);

  const hasTechnicalGroup = !!(
    citationsTotal > 0 || (backlinks && !backlinks.error && !backlinks.skipped) ||
    (pagespeed && !pagespeed.error && !pagespeed.skipped) || (legal && Array.isArray(legal.trackers)) ||
    technicalSeoIssues.length > 0 || (!isDetailed && data.technical_seo_teaser) ||
    adsTrackingIssues.length > 0 || (!isDetailed && data.ads_tracking_teaser)
  );

  return (
    <>
      {providerBarItems.length > 0 && (
        <ReportCard icon={<Sparkles className="h-4 w-4" />} title={L.aiVisibilityGroupTitle || "KI-Sichtbarkeit"}>
          {/* One accordion per provider: the summary row (label/%/bar/"X von
              Y") IS the trigger, expanding it reveals the per-query detail —
              replaces the old layout where the same provider name appeared
              twice (once as a summary bar, again as this accordion's own
              header), which read as duplicated data (user feedback
              2026-09-14). An `unavailable` provider (every query errored)
              has no detail worth expanding, so it renders as a plain row,
              not an accordion item. */}
          <Accordion type="multiple" className="space-y-2">
            {providerBarItems.map((item) =>
              item.unavailable ? (
                <div key={item.key} className="rounded-xl border px-3.5 py-4">
                  <ProviderBarSummary item={item} L={L} />
                </div>
              ) : (
                <AccordionItem key={item.key} value={item.key} className="rounded-xl border px-3.5">
                  <AccordionTrigger><ProviderBarSummary item={item} L={L} /></AccordionTrigger>
                  <AccordionContent>
                    <ProviderDetail visibility={item.visibility} L={L} businessName={data.name} competitorNames={competitorNames} />
                  </AccordionContent>
                </AccordionItem>
              )
            )}
          </Accordion>

          {competitorAiMentions && competitorAiMentions.rows?.length > 0 && competitorAiMentions.total_answers > 0 && (
            <div className="mt-5 border-t pt-4">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.competitorAiTitle}</h3>
              <p className="mb-3 text-sm text-muted-foreground">{L.competitorAiDesc(competitorAiMentions.total_answers)}</p>
              <GroupedBarChart
                items={youVsOthersBarItems(
                  { label: L.you, value: competitorAiMentions.rows.find((r) => r.is_you)?.mentioned_in ?? 0 },
                  competitorAiMentions.rows.filter((r) => !r.is_you).map((r) => ({ label: r.name, value: r.mentioned_in }))
                )}
              />

              {competitorAiMentions.external_domains?.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <h4 className="text-sm font-semibold text-amber-900">{L.externalDomainsTitle}</h4>
                  <p className="mt-0.5 text-sm text-amber-900/80">{L.externalDomainsDesc}</p>
                  <ul className="mt-2 space-y-1">
                    {competitorAiMentions.external_domains.map((d) => (
                      <li key={d.domain} className="flex items-center justify-between gap-2 text-sm text-amber-900">
                        <span className="truncate">{d.domain}</span>
                        <span className="shrink-0 tabular-nums text-amber-900/70">×{d.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <Accordion type="multiple" className="mt-5 space-y-2 border-t pt-4">
            <SourceAttributionSection visibilities={providers.map((p) => p.visibility)} citationSites={citationSites} L={L} />
          </Accordion>

          <ActionSummaryBox summary={aiActionSummary} title={L.aiActionSummaryTitle} />
        </ReportCard>
      )}

      {aiSearchVolume && aiSearchVolume.length > 0 && (
        <ReportCard icon={<TrendingUp className="h-4 w-4" />} title={L.aiVolumeTitle} description={L.aiVolumeDesc}>
          <ul className="space-y-2">
            {aiSearchVolume.map((item, i) => (
              <li key={i} className="flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-secondary/30 p-3 text-base">
                <span className="flex-1">„{item.keyword}"</span>
                <Sparkline values={(item.trend || []).map((t) => t.volume)} />
                <span className="shrink-0 text-sm font-semibold tabular-nums">{L.aiVolumePerMonth(item.ai_search_volume ?? 0)}</span>
              </li>
            ))}
          </ul>
        </ReportCard>
      )}

      {questionGaps && !questionGaps.error && !questionGaps.skipped && questionGaps.total_count > 0 && (
        <ReportCard icon={<ListChecks className="h-4 w-4" />} title={L.questionsTitle} description={L.questionsDesc(questionGaps.seed, questionGaps.covered_count, questionGaps.total_count)}>
          <ul className="space-y-2">
            {questionGaps.questions.map((q, i) => (
              <li key={i} className={`flex items-start gap-2.5 rounded-xl border p-3 text-base ${q.covered ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                {q.covered ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
                <span>{q.question}</span>
              </li>
            ))}
          </ul>
          {questionGaps.local_total_count > 0 && (
            <div className="mt-6 border-t pt-5">
              <h3 className="mb-1.5 flex items-center gap-1.5 font-heading text-base font-semibold"><MapPin className="h-4 w-4" /> {L.localVariantsTitle}</h3>
              <p className="mb-3 text-base text-muted-foreground">{L.localVariantsDesc(questionGaps.local_covered_count, questionGaps.local_total_count)}</p>
              <ul className="space-y-2">
                {questionGaps.local_variants.map((v, i) => (
                  <li key={i} className={`flex items-start gap-2.5 rounded-xl border p-3 text-base ${v.covered ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                    {v.covered ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
                    <span>{v.term}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-4 text-sm text-muted-foreground">{L.questionsSource}</p>
        </ReportCard>
      )}

      {(contentRecs?.articles?.length > 0 || contentRecs?.quick_posts?.length > 0 || missingDirectories.length > 0) && (
        <ReportCard icon={<Lightbulb className="h-4 w-4" />} title={L.contentOpportunitiesTitle}>
          {contentRecs?.articles?.length > 0 && (
            <div>
              <h3 className="mb-3 text-base font-semibold">{L.contentArticlesTitle}</h3>
              <div className="space-y-3">
                {contentRecs.articles.map((a, i) => (
                  <div key={i} className="rounded-xl border border-black/10 bg-secondary/30 p-3.5">
                    <p className="mb-1 text-sm text-muted-foreground">{a.evidence}</p>
                    <h4 className="text-base font-semibold">{a.title}</h4>
                    {a.h2_outline?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                        {a.h2_outline.map((h, j) => <li key={j}>• {h}</li>)}
                      </ul>
                    )}
                    {a.faq?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-sm italic text-muted-foreground">
                        {a.faq.map((f, j) => <li key={j}>? {f}</li>)}
                      </ul>
                    )}
                    <div className="mt-2 flex gap-2">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${IMPACT_EFFORT_CLS[a.impact] || ""}`}>{L.impactLabel(a.impact)}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${IMPACT_EFFORT_CLS[a.effort] || ""}`}>{L.effortLabel(a.effort)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {contentRecs?.quick_posts?.length > 0 && (
            <div className="mt-5 border-t pt-4 first:border-t-0 first:pt-0">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.quickPostsTitle}</h3>
              <ul className="space-y-1.5">
                {contentRecs.quick_posts.map((q, i) => (
                  <li key={i} className="rounded-xl border border-black/10 bg-secondary/30 p-3 text-base">{q.idea}</li>
                ))}
              </ul>
            </div>
          )}

          {missingDirectories.length > 0 && (
            <div className="mt-5 border-t pt-4 first:border-t-0 first:pt-0">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.missingDirectoriesTitle}</h3>
              <ul className="space-y-2">
                {missingDirectories.map((d) => (
                  <li key={d.domain} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-base text-amber-900">
                    <span className="font-medium">{d.label}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-sm">{L.missingDirectoryFoundBy(d.found_by)}</span>
                      {d.signup_url && (
                        <a href={d.signup_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-sm font-semibold text-white hover:bg-amber-700">
                          {L.sourceAttributionRegister}
                        </a>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ReportCard>
      )}

      <KeywordTable keywords={data.keywords} chatgptVisibility={data.chatgpt_visibility} lang={lang} />

      {(mapsRank || organicRank) && (!mapsRank?.error || !organicRank?.error) && (
        <ReportCard
          icon={<MapPin className="h-4 w-4" />}
          title={L.mapsTitle}
          description={L.mapsDesc(mapsRank?.keyword || organicRank?.keyword)}
        >
          <div className="grid grid-cols-2 gap-4">
            {mapsRank && !mapsRank.error && (
              <div>
                {mapsRank.found ? (
                  <StatTile value={`#${mapsRank.rank_absolute}`} label={L.mapsShortLabel} color={tone.fg} />
                ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{L.mapsNotFound(mapsRank.total_checked)}</p>
                )}
              </div>
            )}
            {organicRank && !organicRank.error && (
              <div>
                {organicRank.found ? (
                  <StatTile value={`#${organicRank.rank_absolute}`} label={L.organicShortLabel} color={tone.fg} />
                ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{L.organicNotFound(organicRank.total_checked)}</p>
                )}
              </div>
            )}
          </div>
          {(mapsRank?.found || organicRank?.found) && (
            <p className="mt-3 space-y-0.5 text-sm text-muted-foreground">
              {mapsRank?.found && <span className="block">{L.mapsShortLabel}: {L.rankFound(mapsRank.rank_absolute, mapsRank.total_checked)}</span>}
              {organicRank?.found && <span className="block">{L.organicShortLabel}: {L.rankFound(organicRank.rank_absolute, organicRank.total_checked)}</span>}
            </p>
          )}
          <ActionSummaryBox summary={mapsActionSummary} title={L.mapsActionSummaryTitle} />
        </ReportCard>
      )}

      {gridRank && !gridRank.error && (
        <ReportCard icon={<MapPin className="h-4 w-4" />} title={L.heatmapTitle} description={L.heatmapDesc(gridRank.size, gridRank.keyword)}>
          <GridRankMap gridRank={gridRank} lang={lang} />
        </ReportCard>
      )}

      {hasTechnicalGroup && (
        <Accordion type="multiple" defaultValue={["technical"]} className="mt-6 space-y-2">
          <AccordionItem value="technical" className="rounded-2xl border bg-card px-5 shadow-sm sm:px-6">
            <AccordionTrigger><span className="font-heading text-lg font-semibold">{L.technicalGroupTitle || "Technik & Off-Page-Signale"}</span></AccordionTrigger>
            <AccordionContent>
              {citationsTotal > 0 && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><ListChecks className="h-4 w-4" /> {L.citationsTitle}</h3>
                  <p className="mb-3 text-sm text-muted-foreground">{citationSites ? L.citationsDescDetailed(citationsFound, citationsTotal) : L.citationsDescTeaser(citationsFound, citationsTotal)}</p>
                  {citationSites && (
                    <div className="space-y-4">
                      {[{ kind: "directory", label: L.directories }, { kind: "social", label: L.socialMedia }].map(({ kind, label }) => {
                        const group = checkableCitationSites.filter((s) => (s.kind || "directory") === kind);
                        if (!group.length) return null;
                        return (
                          <div key={kind}>
                            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{label}</h4>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                              {group.map((s) => (
                                <div key={s.domain} className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-sm ${s.found ? "border-green-200 bg-green-50 text-green-900" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                                  {s.found ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" /> : <XCircle className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                                  {s.found && s.url ? (
                                    <a href={s.url} target="_blank" rel="noreferrer" className="truncate font-medium hover:underline" title={s.url}>{s.label}</a>
                                  ) : s.signup_url ? (
                                    <a href={s.signup_url} target="_blank" rel="noreferrer" className="truncate hover:underline" title={L.signUpAt(s.label)}>{s.label}</a>
                                  ) : (
                                    <span className="truncate">{s.label}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {uncheckableCitationSites.length > 0 && (
                        <div>
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.morePlatforms}</h4>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {uncheckableCitationSites.map((s) => (
                              <a key={s.domain} href={s.signup_url} target="_blank" rel="noreferrer" title={`Bei ${s.label} eintragen`} className="flex items-center gap-1.5 truncate rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 text-sm text-blue-900 hover:underline">
                                {s.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {backlinks && !backlinks.error && !backlinks.skipped && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Link2 className="h-4 w-4" /> {L.backlinksTitle}</h3>
                  <p className="text-base text-muted-foreground">{L.backlinksDesc(backlinks.referring_domains, backlinks.backlinks)}</p>
                </div>
              )}

              {pagespeed && !pagespeed.error && !pagespeed.skipped && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Gauge className="h-4 w-4" /> {L.pagespeedTitle}</h3>
                  <p className="mb-3 text-sm text-muted-foreground">{L.pagespeedDesc}</p>
                  <div className="flex flex-wrap gap-x-8 gap-y-3">
                    {[[L.pagespeedMetrics[0], pagespeed.performance_score], [L.pagespeedMetrics[1], pagespeed.seo_score], [L.pagespeedMetrics[2], pagespeed.accessibility_score], [L.pagespeedMetrics[3], pagespeed.best_practices_score]].map(([label, val]) => (
                      <StatTile key={label} value={val ?? "–"} label={label} color={val != null ? scoreTone(val).fg : undefined} />
                    ))}
                  </div>
                  {(pagespeed.lcp_ms != null || pagespeed.cls != null) && (
                    <div className="mt-4 space-y-1.5">
                      {pagespeed.lcp_ms != null && (() => {
                        const t = cwvTone(pagespeed.lcp_ms, [2500, 4000]);
                        return (
                          <div className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${CWV_CLS[t]}`}>
                            <span>{L.lcpLabel}</span>
                            <span className="font-semibold">{(pagespeed.lcp_ms / 1000).toFixed(1)}s — {L.cwv[t]}</span>
                          </div>
                        );
                      })()}
                      {pagespeed.cls != null && (() => {
                        const t = cwvTone(pagespeed.cls, [0.1, 0.25]);
                        return (
                          <div className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${CWV_CLS[t]}`}>
                            <span>{L.clsLabel}</span>
                            <span className="font-semibold">{pagespeed.cls.toFixed(2)} — {L.cwv[t]}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {legal && Array.isArray(legal.trackers) && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><ListChecks className="h-4 w-4" /> {L.analyticsTitle}</h3>
                  <p className="mb-3 text-base text-muted-foreground">{L.analyticsDesc}</p>
                  {legal.trackers.length === 0 ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-base text-amber-900">{L.analyticsNone}</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {legal.trackers.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-sm font-medium text-green-900"><CheckCircle2 className="h-3 w-3" /> {t}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!isDetailed && data.technical_seo_teaser && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Search className="h-4 w-4" /> {L.techSeoTitle}</h3>
                  <p className="text-base text-muted-foreground">{L.techSeoTeaser(data.technical_seo_teaser.positive_count, data.technical_seo_teaser.issue_count)}</p>
                </div>
              )}

              {technicalSeoIssues.length > 0 && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Search className="h-4 w-4" /> {L.techSeoTitle}</h3>
                  <p className="mb-3 text-base text-muted-foreground">{L.techSeoDesc}</p>
                  <div className="space-y-2.5">
                    {technicalSeoIssues.map((f, i) => (
                      <div key={i} className={`rounded-xl border p-3.5 ${SEVERITY_CLS[f.severity] || SEVERITY_CLS.low}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border bg-white/60 px-2 py-0.5 text-xs font-semibold">{L.severity[f.severity] || L.severity.low}</span>
                          <span className="text-base font-semibold">{f.title}</span>
                        </div>
                        <p className="mt-1.5 text-base opacity-90">{f.evidence}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ad conversion-tracking (owner request 2026-10-02): a Meta
                  Pixel or Google Ads tag found on the site is a strong
                  signal the business runs ads there — flagged only when the
                  base tag is present with no conversion event wired up.
                  Static-HTML-only, same withholding pattern as the other
                  non-scored sections above. */}
              {!isDetailed && data.ads_tracking_teaser && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Megaphone className="h-4 w-4" /> {L.adsTrackingTitle}</h3>
                  <p className="text-base text-muted-foreground">{L.adsTrackingTeaser(data.ads_tracking_teaser.issue_count)}</p>
                </div>
              )}

              {adsTrackingIssues.length > 0 && (
                <div className="border-t pt-5 first:border-t-0 first:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-1.5 text-base font-semibold"><Megaphone className="h-4 w-4" /> {L.adsTrackingTitle}</h3>
                  <p className="mb-3 text-base text-muted-foreground">{L.adsTrackingDesc}</p>
                  <div className="space-y-2.5">
                    {adsTrackingIssues.map((f, i) => (
                      <div key={i} className={`rounded-xl border p-3.5 ${SEVERITY_CLS[f.severity] || SEVERITY_CLS.low}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border bg-white/60 px-2 py-0.5 text-xs font-semibold">{L.severity[f.severity] || L.severity.low}</span>
                          <span className="text-base font-semibold">{f.title}</span>
                        </div>
                        <p className="mt-1.5 text-base opacity-90">{f.evidence}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {providerBarItems.length === 0 && !aiSearchVolume && !mapsRank && !organicRank && !gridRank && !hasTechnicalGroup &&
        findings.filter((f) => VISIBILITY_PILLARS.includes(f.pillar)).length === 0 &&
        !(questionGaps && !questionGaps.error && !questionGaps.skipped && questionGaps.total_count > 0) && (
        <div className="rounded-2xl border bg-card p-5 text-base text-muted-foreground shadow-sm sm:p-6">{L.noDataYet}</div>
      )}
    </>
  );
}
